import { AvailableLanguages } from '@repo/toolkit/language-types'
import { TFunction } from 'i18next'
import { ReactNode } from 'react'
import { z, ZodType } from 'zod'

import { GenderEnum } from '../../../../lib/types/enums/common/gender.enum'
import { EnrollmentInformationStatus } from '../../../../lib/types/enums/enrollment/enrollment-information-status.enum'
import { LanguageLevelEnum } from '../../../../lib/types/enums/languages/language-level.enum'
import { MaritalStatusEnum } from '../../../../lib/types/enums/staff-application/marital-status.enum'
import { Enrollment } from '../../../../lib/types/models/enrollment/enrollment'
import { EnrollmentChildInput } from '../../../../lib/types/models/enrollment/enrollment-child-input'
import { EnrollmentInput } from '../../../../lib/types/models/enrollment/enrollment-input'
import { EnrollmentSiblingInput } from '../../../../lib/types/models/enrollment-siblings/enrollment-siblings-input'
import { HowDidYouObtainLecBrochure } from '../../../../lib/types/models/how-did-you-obtain-lec-brochures/how-did-you-obtain-lec-brochure'
import { createEnumSchema } from '../../../../lib/utils/validations/enum-validation'
import {
  createNumberArraySchema,
  createNumberSchema
} from '../../../../lib/utils/validations/int-array-validation'
import { PHONE_NUMBER_SCHEMA } from '../../../../lib/utils/validations/phone-number.validation'
import {
  createStringArraySchema,
  createStringSchema
} from '../../../../lib/utils/validations/string-validation'
import { removeNullUndefined } from '../../../../utils/functions/remove-null-undefined-and-empty-array'
import CountryFlag from '../../../elements/country-flag'

export const ENROLLMENT_SIBLING_INPUT_SCHEMA: ZodType<EnrollmentSiblingInput> = z.object({
  name: createStringSchema('Nom'),
  age: createNumberSchema('Âge'),
  gender: createEnumSchema({ enumType: GenderEnum, fieldName: 'Sexe' })
})

export const clientEnrollmentChildProfileSubmissionSchema = (
  t: TFunction<AvailableLanguages, undefined>,
  isLongVersion: boolean,
  isTermsAndConditionsAccepted: boolean
) => {
  const baseSchema = z.object({
    childEmail: createStringSchema('Email', { isEmail: true, nullish: true }),
    childPhoneNumber: PHONE_NUMBER_SCHEMA(t).nullish(),
    spokenLanguages: createStringArraySchema('Spoken Languages').nullish(),
    childHostFamilyLetter: createStringSchema("Lettre de la famille d'accueil", {
      nullish: true,
      minimumLength: 600
    }),
    agreesToHouseRulesAndNoSmoking: z.boolean().nullish(),
    parentSocialSecurityNumber: createStringSchema('Numéro de sécurité sociale du parent', {
      nullish: true
    }),
    childPreviousTravelOrganization: createStringSchema('Child Previous Travel Organization', {
      nullish: true
    }),
    residenceCountry: createStringSchema('Residence Country', { nullish: true }),
    isChildFirstLanguageStay: z.boolean().nullish(),
    doesChildSmoke: z.boolean().nullish(),
    holdingLECBrochure: z.boolean().nullish(),
    chosenCountryLanguageLevel: createEnumSchema({
      enumType: LanguageLevelEnum,
      fieldName: 'Chosen Country Language Level',
      nullish: true
    }),
    familySituation: createEnumSchema({
      enumType: MaritalStatusEnum,
      fieldName: 'Family Situation',
      nullish: true
    }),
    parentAcceptGeneralPhilosophy: z.boolean().nullish(),
    childAcceptGeneralPhilosophy: z.boolean().nullish(),
    acceptMedicalGeneralTerms: z.boolean().nullish(),
    howDidYouObtainLECBrochureId: createNumberSchema('Comment avez-vous obtenu la brochure', {
      nullish: true
    }),
    hobbies: createStringArraySchema('Hobbies').nullish(),
    characteristics: createNumberArraySchema('Characteristics').nullish(),
    familyImages: z
      .array(
        z.object({
          uploadId: z.string().optional(),
          file: z.any().optional(),
          name: z.string().optional(),
          size: z.number().optional(),
          caption: z.string().optional()
        })
      )
      .nullish(),
    siblings: z.array(ENROLLMENT_SIBLING_INPUT_SCHEMA).nullish(),
    childInfoSubmitted: z.boolean().default(false),
    acceptTermsAndConditions: z.boolean().nullish()
  })

  // ALL validation logic in superRefine - field-level + conditional all trigger together
  return baseSchema.superRefine((data, ctx) => {
    if (data.isChildFirstLanguageStay === null || data.isChildFirstLanguageStay === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Indiquez si c'est un séjour en première langue",
        path: ['isChildFirstLanguageStay']
      })
    }

    if (data.holdingLECBrochure === null || data.holdingLECBrochure === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Indiquez si vous avez la brochure LEC',
        path: ['holdingLECBrochure']
      })
    }

    if (!data.chosenCountryLanguageLevel || data.chosenCountryLanguageLevel.trim() === '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Niveau de langue requis',
        path: ['chosenCountryLanguageLevel']
      })
    }

    // ============ CONDITIONAL VALIDATIONS ============
    if (data.holdingLECBrochure === true && !data.howDidYouObtainLECBrochureId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Comment avez-vous obtenu la brochure LEC est requis si vous avez la brochure',
        path: ['howDidYouObtainLECBrochureId']
      })
    }

    if (data.isChildFirstLanguageStay === true && data.childPreviousTravelOrganization) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Si le séjour est en première langue, l'organisation de voyage précédente n'est pas nécessaire",
        path: ['childPreviousTravelOrganization']
      })
    }

    // ============ SHORT VERSION VALIDATIONS ============
    // the short profile is signed by accepting the terms and conditions, once
    if (
      !isLongVersion &&
      !isTermsAndConditionsAccepted &&
      data.acceptTermsAndConditions !== true
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: t('client.child.profile.terms.required'),
        path: ['acceptTermsAndConditions']
      })
    }

    // ============ LONG VERSION VALIDATIONS ============
    if (isLongVersion) {
      if (data.acceptMedicalGeneralTerms !== true) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Vous devez accepter les conditions médicales',
          path: ['acceptMedicalGeneralTerms']
        })
      }
      if (data.doesChildSmoke === null || data.doesChildSmoke === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Indiquez si l'enfant fume",
          path: ['doesChildSmoke']
        })
      }

      if (data.doesChildSmoke === true && !data.agreesToHouseRulesAndNoSmoking) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Vous devez accepter les règles de la maison et l'interdiction de fumer.",
          path: ['agreesToHouseRulesAndNoSmoking']
        })
      }

      if (!data.childHostFamilyLetter || data.childHostFamilyLetter.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "La Lettre de la famille d'accueil est requise.",
          path: ['childHostFamilyLetter']
        })
      } else if (data.childHostFamilyLetter.trim().length < 600) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'La lettre doit contenir au moins 600 caractères.',
          path: ['childHostFamilyLetter']
        })
      }

      if (data.parentAcceptGeneralPhilosophy !== true) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Vous devez accepter la philosophie des parents',
          path: ['parentAcceptGeneralPhilosophy']
        })
      }

      if (data.childAcceptGeneralPhilosophy !== true) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Vous devez accepter la philosophie de l'enfant",
          path: ['childAcceptGeneralPhilosophy']
        })
      }
    }
  })
}

export const clientEnrollmentChildProfileSaveForLaterSchema = (
  t: TFunction<AvailableLanguages, undefined>
) => {
  return z.object({
    childEmail: createStringSchema('Email', { isEmail: true }),
    childPhoneNumber: PHONE_NUMBER_SCHEMA(t).nullish(),
    spokenLanguages: createStringArraySchema('Spoken Languages'),
    childHostFamilyLetter: createStringSchema('Lettre de la famille d’accueil', { nullish: true }),
    parentSocialSecurityNumber: createStringSchema('Parent Social Security Number', {
      nullish: true
    }),
    agreesToHouseRulesAndNoSmoking: z.boolean().nullish(),
    childPreviousTravelOrganization: createStringSchema('Child Previous Travel Organization', {
      nullish: true
    }),
    residenceCountry: createStringSchema('Residence Country', { nullish: true }),
    isChildFirstLanguageStay: z.boolean().nullish(),
    doesChildSmoke: z.boolean().nullish(),
    holdingLECBrochure: z.boolean().nullish(),

    chosenCountryLanguageLevel: createEnumSchema({
      enumType: LanguageLevelEnum,
      fieldName: 'Chosen Country Language Level',
      nullish: true
    }),

    familySituation: createEnumSchema({
      enumType: MaritalStatusEnum,
      fieldName: 'Family Situation',
      nullish: true
    }),

    parentAcceptGeneralPhilosophy: z.boolean().nullish(),
    childAcceptGeneralPhilosophy: z.boolean().nullish(),
    acceptMedicalGeneralTerms: z.boolean().nullish(),
    howDidYouObtainLECBrochureId: createNumberSchema('Comment avez-vous obtenu la brochure', {
      nullish: true
    }),
    hobbies: createStringArraySchema('Hobbies'),
    characteristics: createNumberArraySchema('Characteristics'),

    familyImages: z
      .array(
        z.object({
          uploadId: z.string().optional(),
          file: z.any().optional(),
          name: z.string().optional(),
          size: z.number().optional(),
          caption: z.string().optional()
        })
      )
      .nullish(),
    siblings: z.array(ENROLLMENT_SIBLING_INPUT_SCHEMA),
    childInfoSubmitted: z.boolean().default(false),
    acceptTermsAndConditions: z.boolean().nullish()
  })
}

export const spokenLanguages: { label: string; flag: ReactNode | undefined }[] = [
  { label: 'Allemand', flag: <CountryFlag code="DEU" /> },
  { label: 'Espagnol', flag: <CountryFlag code="ESP" /> },
  { label: 'Italien', flag: <CountryFlag possibleCountry="Italien" /> }
]

type EnrollmentChildProfileFormProps = Pick<
  Enrollment,
  | 'childEmail'
  | 'childPhoneNumber'
  | 'spokenLanguages'
  | 'childHostFamilyLetter'
  | 'parentSocialSecurityNumber'
  | 'childPreviousTravelOrganization'
  | 'residenceCountry'
  | 'isChildFirstLanguageStay'
  | 'doesChildSmoke'
  | 'holdingLECBrochure'
  | 'chosenCountryLanguageLevel'
  | 'familySituation'
  | 'parentAcceptGeneralPhilosophy'
  | 'childAcceptGeneralPhilosophy'
  | 'acceptMedicalGeneralTerms'
  | 'hobbies'
  | 'agreesToHouseRulesAndNoSmoking'
  | 'childInfoStatus'
> & {
  secureFamilyImages: Pick<
    Enrollment['secureFamilyImages'][number],
    'id' | 'uploadId' | 'name' | 'size' | 'caption'
  >[]
  characteristics: Pick<Enrollment['characteristics'][number], 'id'>[]
  siblings: Pick<Enrollment['siblings'][number], 'id' | 'name' | 'age' | 'gender'>[]
  howDidYouObtainLECBrochure?: Pick<HowDidYouObtainLecBrochure, 'id'>
}
export const mapEnrollmentExpandedToEnrollmentChildInput = (
  enrollment?: EnrollmentChildProfileFormProps
): EnrollmentChildInput | undefined => {
  if (!enrollment) return undefined
  return removeNullUndefined({
    childEmail: enrollment.childEmail,
    childPhoneNumber: enrollment.childPhoneNumber,
    spokenLanguages: enrollment.spokenLanguages,
    childHostFamilyLetter: enrollment.childHostFamilyLetter,
    parentSocialSecurityNumber: enrollment.parentSocialSecurityNumber,
    childPreviousTravelOrganization: enrollment.childPreviousTravelOrganization,
    residenceCountry: enrollment.residenceCountry,
    isChildFirstLanguageStay: enrollment.isChildFirstLanguageStay,
    doesChildSmoke: enrollment.doesChildSmoke,
    holdingLECBrochure: enrollment.holdingLECBrochure,
    chosenCountryLanguageLevel: enrollment.chosenCountryLanguageLevel,
    familySituation: enrollment.familySituation,
    parentAcceptGeneralPhilosophy: enrollment.parentAcceptGeneralPhilosophy,
    childAcceptGeneralPhilosophy: enrollment.childAcceptGeneralPhilosophy,
    acceptMedicalGeneralTerms: enrollment.acceptMedicalGeneralTerms,
    howDidYouObtainLECBrochureId: enrollment.howDidYouObtainLECBrochure?.id,
    hobbies: enrollment.hobbies?.map((hobby) => hobby.name),
    characteristics: enrollment.characteristics?.map((characteristic) => characteristic.id),
    familyImages: enrollment.secureFamilyImages.map((file) => ({
      id: file.id,
      uploadId: file.uploadId,
      caption: file.caption,
      name: file.name,
      size: file.size
    })),
    siblings: enrollment.siblings.map((sibling) => ({
      id: sibling.id,
      name: sibling.name,
      age: sibling.age,
      gender: sibling.gender
    })),
    agreesToHouseRulesAndNoSmoking: enrollment.agreesToHouseRulesAndNoSmoking,
    childInfoSubmitted: enrollment.childInfoStatus !== EnrollmentInformationStatus.PENDING
  })
}

export const mapEnrollmentChildInputToEnrollmentRequest = (
  input: EnrollmentChildInput,
  isLongVersion: boolean
): Partial<EnrollmentInput> => {
  const normalizeBool = (v?: boolean | null): boolean | undefined => (v === null ? undefined : v)

  const commonFields = {
    childEmail: input.childEmail,
    childPhoneNumber: input.childPhoneNumber,
    spokenLanguages: input.spokenLanguages,
    parentSocialSecurityNumber: input.parentSocialSecurityNumber,
    childPreviousTravelOrganization: input.childPreviousTravelOrganization,
    residenceCountry: input.residenceCountry,
    isChildFirstLanguageStay: input.isChildFirstLanguageStay,
    doesChildSmoke: input.doesChildSmoke,
    holdingLECBrochure: input.holdingLECBrochure,
    chosenCountryLanguageLevel: input.chosenCountryLanguageLevel,
    familySituation: input.familySituation,
    howDidYouObtainLECBrochureId: input.howDidYouObtainLECBrochureId,
    childInfoSubmitted: input.childInfoSubmitted,
    hobbies: input.hobbies,
    siblings: input.siblings,
    characteristics: input.characteristics,
    agreesToHouseRulesAndNoSmoking: normalizeBool(input.agreesToHouseRulesAndNoSmoking)
  }
  // the acceptance is only sent with the submission, the backend stamps it once
  const shortVersionFields = {
    acceptTermsAndConditions:
      input.childInfoSubmitted && input.acceptTermsAndConditions === true ? true : undefined
  }
  const longVersionFields = {
    acceptMedicalGeneralTerms: normalizeBool(input.acceptMedicalGeneralTerms),
    childHostFamilyLetter: input.childHostFamilyLetter,
    parentAcceptGeneralPhilosophy: normalizeBool(input.parentAcceptGeneralPhilosophy),
    childAcceptGeneralPhilosophy: normalizeBool(input.childAcceptGeneralPhilosophy),
    familyImages: input.familyImages
  }
  return {
    ...commonFields,
    ...(isLongVersion ? longVersionFields : shortVersionFields)
  }
}
